import { Component, Input, OnInit, signal, computed, inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatPaginatorModule, MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSortModule, MatSort, Sort } from '@angular/material/sort';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { FormsModule } from '@angular/forms';
import {
  ParkingProcessService,
  ParkingProcessListItemDto,
  ParkingSpaceProcessesSummaryDto,
  FetchParkingSpaceProcessesRequest,
  ZoneProcessSummaryDto,
  ParkingProcessDetectionLogDto,
  LicensePlateDto,
  ParkingSpaceService,
  ParkingSpaceDto,
  ParkingZoneDto,
  PaymentService,
  PaymentSessionDto
} from '../../../api/gen';
import { JJamesCardComponent, JJamesBadgeComponent, BadgeVariant, ResponsiveBaseComponent, Role } from 'shared-lib';
import { RoleService } from '../../auth/role-service';
import { LicensePlateCacheService } from '../../shared/license-plate-cache.service';
import { provideGermanDateAdapter } from './german-date-adapter';

export type ProcessFilterType = 'all' | 'active' | 'completed';

// Extend ParkingProcessListItemDto to allow manualEndDate for UI
interface ParkingProcessListItemWithManualEndDate extends ParkingProcessListItemDto {
  manualEndDate?: string;
}

@Component({
  selector: 'app-parking-space-process-details',
  imports: [
    CommonModule,
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatButtonModule,
    MatChipsModule,
    MatTooltipModule,
    MatButtonToggleModule,
    MatSelectModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatDatepickerModule,
    MatSlideToggleModule,
    FormsModule,
    JJamesCardComponent,
    JJamesBadgeComponent
  ],
  providers: [provideGermanDateAdapter()],
  templateUrl: './parking-space-process-details.component.html',
  styleUrl: './parking-space-process-details.component.scss'
})
export class ParkingSpaceProcessDetailsComponent extends ResponsiveBaseComponent implements OnInit {
  private parkingProcessService = inject(ParkingProcessService);
  private parkingSpaceService = inject(ParkingSpaceService);
  private licensePlateCache = inject(LicensePlateCacheService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private roleService = inject(RoleService);
  private paymentService = inject(PaymentService);

  @Input() parkingSpaceId!: string;

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  // Data
  summary = signal<ParkingSpaceProcessesSummaryDto | null>(null);
  dataSource = new MatTableDataSource<ParkingProcessListItemDto>([]);

  // Expandable row
  expandedElement: ParkingProcessListItemDto | null = null;

  // State
  isLoading = signal(false);
  selectedFilter = signal<ProcessFilterType>('all');

  // Parking space (loaded from API for zone list)
  parkingSpace = signal<ParkingSpaceDto | null>(null);

  // LP autocomplete — filters the app-wide plate list loaded after login; no request per keystroke
  isLoadingLps = this.licensePlateCache.isLoading;
  lpSearchTerm = signal('');
  selectedLicensePlateId = signal<string | null>(null);
  filteredLpOptions = computed(() => this.licensePlateCache.search(this.lpSearchTerm()));

  // Search filters
  zoneFilter = signal('');
  startDateFilter = signal('');
  endDateFilter = signal('');

  // Session time / covered-zero correction — JJames administrators and operator admins; the backend
  // limits an operator admin to stays at their own parking spaces.
  canEditTimes = signal(false);
  editingTimesId = signal<string | null>(null);
  editStartDate = signal<Date | null>(null);
  editStartTime = signal('');
  editEndDate = signal<Date | null>(null);
  editEndTime = signal('');
  editTimesReason = signal('');
  editCoveredZero = signal(false);
  timesError = signal<string | null>(null);
  isSavingTimes = signal(false);

  // Payment per stay, fetched once when its row is first expanded. null = no payment session.
  payments = signal<Record<string, PaymentSessionDto | null>>({});

  // Pagination
  totalCount = signal(0);
  pageSize = 10;
  pageIndex = 0;

  // Table columns
  displayedColumns: string[] = [
    'expand',
    'status',
    'licensePlate',
    'zone',
    'startedAt',
    'duration',
    'violation'
  ];

  async ngOnInit(): Promise<void> {
    // Get parkingSpaceId from route params if not provided as input
    if (!this.parkingSpaceId) {
      this.parkingSpaceId = this.route.snapshot.paramMap.get('id') || '';
    }

    if (this.parkingSpaceId) {
      this.loadParkingSpace();
      this.loadProcesses();
    }
    this.licensePlateCache.load();
    this.canEditTimes.set(
      await this.roleService.checkAccess(Role.Administrator) ||
      await this.roleService.checkAccess(Role.OperatorAdmin));
  }

  loadParkingSpace(): void {
    this.parkingSpaceService.parkingSpaceParkingSpaceIdGet(this.parkingSpaceId).subscribe({
      next: (space) => this.parkingSpace.set(space),
      error: (err) => console.error('Failed to load parking space', err)
    });
  }

  onLpSearchChange(value: string | LicensePlateDto): void {
    if (typeof value === 'object' && value !== null) {
      this.lpSearchTerm.set(value.fullNumber || '');
      this.selectedLicensePlateId.set(value.id || null);
      return;
    }
    const strVal = typeof value === 'string' ? value : '';
    this.lpSearchTerm.set(strVal);
    // If user clears the field, also clear the selection
    if (!strVal) {
      this.selectedLicensePlateId.set(null);
    }
  }

  onLpSelected(lp: LicensePlateDto): void {
    if (!lp) return;
    this.selectedLicensePlateId.set(lp.id || null);
    this.lpSearchTerm.set(lp.fullNumber || '');
  }

  displayLp = (lp: LicensePlateDto | string): string => {
    if (!lp) return '';
    if (typeof lp === 'string') return lp;
    return lp.fullNumber || '';
  };

  get availableZones(): ParkingZoneDto[] {
    return this.parkingSpace()?.parkingZones || [];
  }

  goBack(): void {
    this.router.navigate(['/parking-space']);
  }

  toggleRow(process: ParkingProcessListItemDto): void {
    this.expandedElement = this.expandedElement === process ? null : process;
    if (this.expandedElement) this.loadPayment(process);
  }

  private loadPayment(process: ParkingProcessListItemDto): void {
    const id = process.id;
    if (!id || id in this.payments()) return;
    this.payments.update(p => ({ ...p, [id]: null }));
    // 404 = the stay never had a payment session; 403 = role cannot see payments. Both show nothing.
    this.paymentService.paymentsByProcessParkingProcessIdGet(id).subscribe({
      next: session => this.payments.update(p => ({ ...p, [id]: session })),
      error: () => { }
    });
  }

  getPayment(process: ParkingProcessListItemDto): PaymentSessionDto | null {
    return process.id ? this.payments()[process.id] ?? null : null;
  }

  formatAmount(session: PaymentSessionDto): string {
    return new Intl.NumberFormat('de-AT', { style: 'currency', currency: session.currency || 'EUR' })
      .format(session.amount ?? 0);
  }

  loadProcesses(): void {
    if (!this.parkingSpaceId) return;

    this.isLoading.set(true);

    const request: FetchParkingSpaceProcessesRequest = {
      pageSize: this.pageSize,
      pageIndex: this.pageIndex,
      activeOnly: this.selectedFilter() === 'active' ? true :
        this.selectedFilter() === 'completed' ? false : null,
      includeDeleted: false,
      licensePlateId: this.selectedLicensePlateId() || undefined,
      parkingZoneId: this.zoneFilter() || undefined,
      startedFrom: this.startDateFilter() ? new Date(this.startDateFilter()).toISOString() : undefined,
      startedTo: this.endDateFilter() ? new Date(this.endDateFilter() + 'T23:59:59').toISOString() : undefined
    };

    this.parkingProcessService.parkingProcessParkingSpaceParkingSpaceIdPost(
      this.parkingSpaceId,
      request
    ).subscribe({
      next: (result) => {
        this.summary.set(result);
        this.dataSource.data = result.processes?.items || [];
        this.totalCount.set(result.processes?.totalCount || 0);
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Error loading parking processes:', error);
        this.isLoading.set(false);
      }
    });
  }

  onFilterChange(filter: ProcessFilterType): void {
    this.selectedFilter.set(filter);
    this.pageIndex = 0;
    this.loadProcesses();
  }

  applySearchFilters(): void {
    this.pageIndex = 0;
    this.loadProcesses();
  }

  clearFilters(): void {
    this.lpSearchTerm.set('');
    this.selectedLicensePlateId.set(null);
    this.zoneFilter.set('');
    this.startDateFilter.set('');
    this.endDateFilter.set('');
    this.selectedFilter.set('all');
    this.pageIndex = 0;
    this.loadProcesses();
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadProcesses();
  }

  onSortChange(sort: Sort): void {
    // Implement sorting logic if needed
    this.loadProcesses();
  }

  refresh(): void {
    this.loadProcesses();
  }

  stopProcess(process: ParkingProcessListItemWithManualEndDate): void {
    if (!process.manualEndDate) {
      alert('Please select an end date.');
      return;
    }
    this.isLoading.set(true);
    this.parkingProcessService.parkingProcessParkingProcessIdPut(
      process.id!,
      {
        endedAt: new Date(process.manualEndDate).toISOString()
      }
    ).subscribe({
      next: (result) => {
        process.endedAt = result.endedAt;
        this.isLoading.set(false);
        this.loadProcesses();
      },
      error: (error) => {
        alert('Failed to stop process.');
        this.isLoading.set(false);
      }
    });
  }

  startEditTimes(process: ParkingProcessListItemDto): void {
    this.editingTimesId.set(process.id ?? null);
    this.editStartDate.set(process.startedAt ? new Date(process.startedAt) : null);
    this.editStartTime.set(this.toTimeInput(process.startedAt));
    this.editEndDate.set(process.endedAt ? new Date(process.endedAt) : null);
    this.editEndTime.set(this.toTimeInput(process.endedAt));
    this.editTimesReason.set('');
    this.editCoveredZero.set(process.isCoveredZero === true);
    this.timesError.set(null);
  }

  cancelEditTimes(): void {
    this.editingTimesId.set(null);
    this.timesError.set(null);
  }

  saveTimes(process: ParkingProcessListItemDto): void {
    const start = this.combine(this.editStartDate(), this.editStartTime());
    if (!start) {
      this.timesError.set('Start needs a date and a time (HH:MM).');
      return;
    }
    const hasEnd = !!this.editEndDate() || !!this.editEndTime().trim();
    const end = hasEnd ? this.combine(this.editEndDate(), this.editEndTime()) : null;
    if (hasEnd && !end) {
      this.timesError.set('End needs a date and a time (HH:MM), or leave both empty.');
      return;
    }
    const startedAt = start.toISOString();
    const endedAt = end ? end.toISOString() : undefined;
    if (endedAt && endedAt <= startedAt) {
      this.timesError.set('End must be after start.');
      return;
    }

    this.isSavingTimes.set(true);
    this.timesError.set(null);
    this.parkingProcessService.parkingProcessParkingProcessIdTimesPut(process.id!, {
      startedAt,
      endedAt,
      isCoveredZero: this.editCoveredZero(),
      amendmentDetail: this.editTimesReason().trim() || undefined
    }).subscribe({
      next: () => {
        this.isSavingTimes.set(false);
        this.editingTimesId.set(null);
        this.loadProcesses();
      },
      error: (err) => {
        this.isSavingTimes.set(false);
        this.timesError.set(err?.error?.detail ?? err?.error?.title ?? 'Failed to update times.');
      }
    });
  }

  /** ISO timestamp → HH:MM in the browser's local time. */
  private toTimeInput(iso: string | null | undefined): string {
    if (!iso) return '';
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }

  /** Datepicker day + typed HH:MM → local Date; null if either is missing or malformed. */
  private combine(date: Date | null, time: string): Date | null {
    const match = /^([01]?\d|2[0-3]):([0-5]\d)$/.exec(time.trim());
    if (!date || isNaN(date.getTime()) || !match) return null;
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), +match[1], +match[2]);
  }

  // Helper methods
  getStatusBadgeVariant(process: ParkingProcessListItemDto): BadgeVariant {
    return process.isActive ? 'success' : 'secondary';
  }

  getStatusText(process: ParkingProcessListItemDto): string {
    return process.isActive ? 'Active' : 'Completed';
  }

  getLicensePlate(process: ParkingProcessListItemDto): string {
    return process.licensePlateDetails?.fullNumber || 'N/A';
  }

  getParkerName(process: ParkingProcessListItemDto): string {
    return process.parkerDetails?.displayName || 'N/A';
  }

  getZoneName(process: ParkingProcessListItemDto): string {
    return process.parkingZoneDetails?.name || 'N/A';
  }

  /**
   * The policy strategy that decided this stay — 'Long Term Parker', 'Short Term Parker',
   * 'Pool Parker', 'Default Strategy' and so on. Blank when the process predates strategy
   * recording, which is why the column falls back to a dash rather than inventing a name.
   */
  getStrategyName(process: ParkingProcessListItemDto): string {
    return process.strategyDetails?.name || 'N/A';
  }

  formatDate(dateStr: string | null | undefined): string {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toUTCString();
  }

  formatDuration(duration: string | null | undefined): string {
    if (!duration) return '-';
    // Handle TimeSpan format: D.HH:mm:ss.ffffff or HH:mm:ss
    let days = 0, hours = 0, minutes = 0;
    let main = duration;
    // Split fractional seconds
    if (main.includes('.')) {
      const firstDot = main.indexOf('.');
      const beforeDot = main.substring(0, firstDot);
      const afterDot = main.substring(firstDot + 1);
      if (beforeDot.includes(':')) {
        main = beforeDot;
      } else {
        days = parseInt(beforeDot, 10);
        main = afterDot.split('.')[0]; // Remove any further fractional part
      }
    }
    const parts = main.split(':');
    if (parts.length === 3) {
      hours = parseInt(parts[0], 10);
      minutes = parseInt(parts[1], 10);
      // seconds = parseInt(parts[2], 10); // Ignore seconds
    } else if (parts.length === 2) {
      hours = parseInt(parts[0], 10);
      minutes = parseInt(parts[1], 10);
    }
    const partsArr: string[] = [];
    if (days > 0) partsArr.push(`${days} day${days > 1 ? 's' : ''}`);
    if (hours > 0) partsArr.push(`${hours} hour${hours > 1 ? 's' : ''}`);
    if (minutes > 0) partsArr.push(`${minutes} minute${minutes > 1 ? 's' : ''}`);
    if (partsArr.length === 0) return '0 minutes';
    return partsArr.join(', ');
  }

  getShortId(id: string | undefined): string {
    if (!id) return 'N/A';
    return id.substring(0, 8) + '...';
  }

  hasViolation(process: ParkingProcessListItemDto): boolean {
    return process.hasViolation || false;
  }

  getViolationCount(process: ParkingProcessListItemDto): number {
    return process.violationDetails?.violationCount || 0;
  }

  getDetectionLogs(process: ParkingProcessListItemDto): ParkingProcessDetectionLogDto[] {
    return process.detectionLogs || [];
  }

  getDetectionLogCount(process: ParkingProcessListItemDto): number {
    return process.detectionLogs?.length || 0;
  }

  getDetectionTypeBadgeVariant(type: string | undefined): BadgeVariant {
    switch (type) {
      case 'Start': return 'success';
      case 'Stop': return 'secondary';
      case 'Update': return 'info';
      case 'ExitRefused': return 'warning';
      default: return 'secondary';
    }
  }

  // Image preview
  previewImageUrl = signal<string | null>(null);

  openPreview(url: string): void {
    this.previewImageUrl.set(url);
  }

  closePreview(): void {
    this.previewImageUrl.set(null);
  }
}
